// FormSettingsDrawer — opened from the builder's "Form settings" button. A side
// Drawer with four tabs; each tab's content lives in its own component and owns
// its own save (its own API call). The shared footer "Save changes" delegates to
// the active tab via an imperative handle; "Discard" closes. Form versions is
// read-only (no Save button); the External report tab is capability-gated.

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Button, Drawer, Tabs } from '@/components/common';
import { useCanExternalApiExportFormTemplates } from '@/hooks/useModuleAccess';
import type { FormDetail } from '@/api/endpoints/formsApi';
import FormSettingsSetup from './FormSettingsSetup';
import FormSettingsReportLayout from './FormSettingsReportLayout';
import FormSettingsExternalReport from './FormSettingsExternalReport';
import FormSettingsVersions from './FormSettingsVersions';

/** Each tab exposes an imperative save; returns true when persisted, false on a
 *  client validation failure (drawer stays open), throws on an API error. */
export interface FormSettingsTabHandle {
  save: () => Promise<boolean>;
}

interface FormSettingsDrawerProps {
  open: boolean;
  onClose: () => void;
  /** The form being edited (save target). */
  formId: string;
  /** Form detail — seeds the tabs (undefined while it loads). */
  detail?: FormDetail;
}

type SettingsTab = 'setup' | 'report-layout' | 'external-report' | 'form-versions';

const FormSettingsDrawer = ({ open, onClose, formId, detail }: FormSettingsDrawerProps) => {
  const { t } = useTranslation();
  const canExternalReport = useCanExternalApiExportFormTemplates();
  const [tab, setTab] = useState<SettingsTab>('setup');
  const [saving, setSaving] = useState(false);
  const activeRef = useRef<FormSettingsTabHandle>(null);

  // Reset to the Setup tab each time the drawer opens.
  useEffect(() => {
    if (open) setTab('setup');
  }, [open]);

  const save = async () => {
    // Every savable tab attaches the handle; null only in the attach race.
    const handle = activeRef.current;
    if (!handle) return;
    setSaving(true);
    try {
      const saved = await handle.save();
      if (saved) {
        toast.success(t('form_settings_saved'));
        onClose();
      }
    } catch {
      toast.error(t('error_generic'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      className='form-settings'
      icon='sliders-horizontal'
      title={t('form_settings_title')}
      footer={
        <>
          <Button variant='secondary' onClick={onClose} disabled={saving}>
            {t('form_settings_discard')}
          </Button>
          {tab !== 'form-versions' && (
            <Button iconStart='check' onClick={save} loading={saving}>
              {t('form_settings_save')}
            </Button>
          )}
        </>
      }
    >
      <Tabs
        aria-label={t('form_settings_title')}
        value={tab}
        onChange={(v) => setTab(v as SettingsTab)}
        items={[
          { value: 'setup', label: t('form_settings_tab_setup'), icon: 'sliders-horizontal' },
          {
            value: 'report-layout',
            label: t('form_settings_tab_report'),
            icon: 'layout-dashboard',
          },
          ...(canExternalReport
            ? [
                {
                  value: 'external-report',
                  label: t('form_settings_tab_external'),
                  icon: 'share-2',
                },
              ]
            : []),
          { value: 'form-versions', label: t('form_settings_tab_versions'), icon: 'history' },
        ]}
      />

      {tab === 'setup' ? (
        <FormSettingsSetup
          ref={activeRef}
          formId={formId}
          initialTitle={detail?.name}
          initialDescription={detail?.description}
          initialIcon={detail?.icon}
          initialColor={detail?.submitButtonColor}
          initialWorkflow={detail?.workflow}
          initialWorkflowVersion={detail?.workflowVersion}
          initialFolder={detail?.folder}
        />
      ) : tab === 'report-layout' ? (
        <FormSettingsReportLayout ref={activeRef} formId={formId} initial={detail?.reportLayout} />
      ) : tab === 'form-versions' ? (
        <FormSettingsVersions detail={detail} />
      ) : (
        <FormSettingsExternalReport
          ref={activeRef}
          formId={formId}
          initial={detail?.apiExport}
          design={detail?.design}
        />
      )}
    </Drawer>
  );
};

export default FormSettingsDrawer;
